import { atom, useAtom, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback } from 'react';

import { useOpenAuthDialog } from '@/components/Modals/AuthDialog/useOpenAuthDialog';

import { useStage } from './useStage';

import type { BillingMode } from '@/atoms/billing';
import type { SceneKey } from './types';

const FREE_PLAN_TYPE = 'free';

export type PendingPricingPlan = {
  planType: string;
  billingPeriod: BillingMode;
} | null;

/**
 * Plan/period picked from a pricing card, stashed while the user is in the
 * sign-up pop-up so `usePricingSignupSuccessRedirect` knows to route to
 * checkout once the account exists. Cleared once consumed.
 */
export const pendingPricingPlanAtom = atom<PendingPricingPlan>(null);

/**
 * sessionStorage rather than a Jotai atom: Stripe's offsite payment methods
 * (PayPal, 3DS, iDEAL, …) navigate the browser away from /subscribe and
 * back, a full page load that would wipe in-memory atom state.
 */
export const PRICING_SIGNUP_QUESTIONNAIRE_KEY =
  'pricingSignupQuestionnairePending';

/**
 * The page the user was on right before being routed to /subscribe (e.g.
 * `/pricing`, `/`). sessionStorage for the same reason as above — the
 * success screen's CTA reads this after the Stripe round trip to send the
 * user back where they started.
 */
export const CHECKOUT_ORIGIN_KEY = 'checkoutOriginPath';

/**
 * Stashes the current page as the checkout origin, then routes to
 * `/subscribe` for the given plan/period. Every call site that sends a user
 * to checkout should go through this so the success screen's CTA always has
 * somewhere to send them back to.
 */
export const useNavigateToCheckout = () => {
  const router = useRouter();

  return useCallback(
    (planType: string, billingPeriod: BillingMode) => {
      sessionStorage.setItem(CHECKOUT_ORIGIN_KEY, router.asPath);
      void router.push(
        `/subscribe?plan=${planType.toLowerCase()}&period=${billingPeriod}`
      );
    },
    [router]
  );
};

/**
 * Opens the central sign-up pop-up for a pricing-card plan selection. Paid
 * plans stash the plan/period so a successful signup routes to checkout;
 * Free has no checkout step, so nothing is stashed and signup falls through
 * to the normal onboarding gate.
 */
export const useOpenPricingSignupDialog = () => {
  const setPendingPlan = useSetAtom(pendingPricingPlanAtom);
  const openAuthDialog = useOpenAuthDialog();

  return useCallback(
    (planType: string, billingPeriod: BillingMode) => {
      if (planType.toLowerCase() !== FREE_PLAN_TYPE) {
        setPendingPlan({ planType, billingPeriod });
      }
      openAuthDialog({ context: 'pricing-plan' });
    },
    [setPendingPlan, openAuthDialog]
  );
};

/**
 * Call from a signup scene's `onSignupSuccess`. When the signup came from a
 * pricing-card plan pick, closes the stage and routes to `/subscribe`
 * checkout for that plan; otherwise falls back to the scene's normal
 * post-signup onboarding gate.
 */
export const usePricingSignupSuccessRedirect = (
  goTo: (key: SceneKey) => void
) => {
  const [pendingPlan, setPendingPlan] = useAtom(pendingPricingPlanAtom);
  const { close } = useStage();
  const navigateToCheckout = useNavigateToCheckout();

  return useCallback(() => {
    if (!pendingPlan) {
      goTo('onboarding.auth-gate');
      return;
    }

    sessionStorage.setItem(PRICING_SIGNUP_QUESTIONNAIRE_KEY, '1');
    setPendingPlan(null);
    close();
    navigateToCheckout(pendingPlan.planType, pendingPlan.billingPeriod);
  }, [pendingPlan, setPendingPlan, close, navigateToCheckout, goTo]);
};
